import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Row, Col, Card, Tag, Spin, Button } from "antd";
import DefaultLayout from "../../components/layout/DefaultLayout";
import Header from "../../components/header";
import { FormOutlined } from "@ant-design/icons";
import dynamicFormService from "../../services/dynamicForm";

// Component to display "Created By" info
const CreatedByComponent = ({ record }) => {
  if (!record) return null;
  return (
    <Card title="Created By" style={{ marginBottom: 16 }}>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={24} md={24}><b>Name:</b> <Tag>{record.core?.created_by_name || "-"}</Tag></Col>
        <Col xs={24} sm={24} md={24}><b>Email:</b> <Tag>{record.core?.email || "-"}</Tag></Col>
        <Col xs={24} sm={24} md={24}><b>Employee No:</b> <Tag>{record.core?.employee_no || "-"}</Tag></Col>
        <Col xs={24} sm={24} md={24}><b>Department:</b> <Tag>{record.core?.department_name || "-"}</Tag></Col>
        <Col xs={24} sm={24} md={24}><b>Section:</b> <Tag>{record.core?.section_name || "-"}</Tag></Col>
        <Col xs={24} sm={24} md={24}><b>Designation:</b> <Tag>{record.core?.designation_name || "-"}</Tag></Col>
        <Col xs={24} sm={24} md={24}><b>Created date:</b> <Tag>{record.core?.created_at || "-"}</Tag></Col>
      </Row>
    </Card>
  );
};

// Helper to render custom field value based on type
const renderCustomFieldValue = (field) => {
  if (!field.value) return "-";
  switch (field.type) {
    case "text":
    case "textarea":
      return <span>{field.value}</span>;
    case "select":
    case "radio":
      return <Tag>{field.value}</Tag>;
    case "checkbox":
    case "multiselect":
      return Array.isArray(field.value)
        ? field.value.map((v, idx) => <Tag key={idx}>{v}</Tag>)
        : field.value.toString().split(",").map((v, idx) => <Tag key={idx}>{v}</Tag>);
    case "file":
      return field.value ? <a href={field.value} target="_blank" rel="noreferrer">View File</a> : "-";
    case "date":
      return field.value ? new Date(field.value).toLocaleDateString() : "-";
    default:
      return <span>{field.value}</span>;
  }
};

const DynamicFormDetails = () => {
  const { slug, id } = useParams();
  const [loading, setLoading] = useState(false);
  const [record, setRecord] = useState(null);
  const [formInfo, setFormInfo] = useState(null);

  useEffect(() => {
    fetchDetails();
  }, [slug, id]);

  const fetchDetails = async () => {
    try {
      setLoading(true);
      const form = await dynamicFormService.getFormBySlug(slug);
      setFormInfo(form);
      const rec = await dynamicFormService.getRecordById(slug, id);
      setRecord(rec);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !record || !formInfo)
    return (
      <DefaultLayout>
        <div style={{ textAlign: "center", padding: 50 }}>
          <Spin size="large" />
        </div>
      </DefaultLayout>
    );

  // Map custom fields with labels from form definition
  const customFields = formInfo.fields.map((field) => ({
    ...field,
    value: record.custom_fields?.[field.id] ?? "", // match by field ID
  }));

  return (
    <DefaultLayout>
      <Row gutter={[24, 24]}>
        <Col span={24}>
          <Header icon={<FormOutlined />} title={`${formInfo.name || formInfo.title} - Details`} />
        </Col>
      </Row>

      <Card style={{ minHeight: "100vh" }}>
        <Row gutter={[24, 24]}>
          {/* Left Column */}
          <Col lg={5} md={5} sm={7} xs={24}>
            <CreatedByComponent record={record} />

            <Button
              type="primary"
              style={{ marginTop: 20, marginBottom: 20, width: "100%" }}
            >
              Print Document
            </Button>

            {/* Approvers Component placeholder */}
            {/* <ApproversComponent approvers={record.approvers} /> */}
          </Col>

          {/* Right Column */}
          <Col lg={19} md={19} sm={17} xs={24}>
            {/* Basic Information */}
            <Card title="Basic Information" className="mb-10">
              <Row gutter={[16, 16]}>
                {[
                  "Sequence No", "Request Title", "Status", "Issue Date",
                  "Receive Date", "Make", "Model", "IMEI",
                  "Mobile Number", "Remarks", "Request For User"
                ].map((label, idx) => (
                  <Col xs={24} sm={12} md={8} lg={6} key={idx}>
                    <b>{label}:</b>{" "}
                    <Tag>
                      {record.core?.[label.replace(/\s+/g, "_").toLowerCase()] || "-"}
                    </Tag>
                  </Col>
                ))}
              </Row>
            </Card>

            {/* Custom Fields */}
            <Card title="Custom Fields" className="mb-10">
              <Row gutter={[16, 16]}>
                {customFields.map((field) => (
                  <Col xs={24} sm={12} md={8} lg={6} key={field.id}>
                    <b>{field.label}:</b> {renderCustomFieldValue(field)}
                  </Col>
                ))}
              </Row>
            </Card>

            {/* Comments Placeholder */}
            {/* <Card title="Comments">
              <Input.TextArea placeholder="Type comment..." rows={3} />
            </Card> */}
          </Col>
        </Row>
      </Card>
    </DefaultLayout>
  );
};

export default DynamicFormDetails;
